import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialogActions,
  AlertDialogBody,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  useAlertDialog,
} from '@clay-run/glaze/alert-dialog';
import { Button } from '@clay-run/glaze/button';

import { useUnsavedChangesWarning } from '../../_shared/hooks';
import { clearFlowSession, useFlowSessionStore } from '../flow-session-store';
import {
  clearGeneratedCopyReviews,
  clearGeneratedCopyReviewsInScope,
  useGeneratedCopyStore,
} from '../generated-copy-store';
import { flowContextKeyFromHref } from '../play-flow-context';
import {
  clearCopyDraftsInScope,
  clearSequenceCopyDrafts,
  useSequenceCopyDraftStore,
} from '../sequence-copy-draft-store';
import {
  selectHasCopyDrafts,
  selectHasCopyDraftsInScope,
  selectHasReadyGeneratedCopy,
  selectHasReadyGeneratedCopyInScope,
} from '../unsaved-guard';

/**
 * Round-4 U7 (E3) — the "don't silently throw away my work" guard, mounted
 * once per editing surface.
 *
 * TWO EXITS, TWO MECHANISMS, because a React Router navigation and a real
 * browser unload are different events:
 *
 *  - Tab close / refresh / typing a new URL → the native `beforeunload`
 *    prompt, via core's `useUnsavedChangesWarning` (re-exported for
 *    mini-apps at `_shared/hooks/useUnsavedChangesWarning.ts`; mini-apps may
 *    not import `web/` directly).
 *  - A click on one of `PlaybooksLayout`'s header Links → a capture-phase
 *    document listener that gets there before React Router's own click
 *    handler and opens the Glaze `AlertDialog` instead.
 *  - A surface's OWN imperative `navigate(...)` → there is no click and no
 *    anchor for the listener to see, so those call sites have to opt in by
 *    routing through {@link useGuardedNavigate} themselves (round-4 QA
 *    codex-review fix, wave 1, finding 5; `PlaybooksConfigPage`'s row "Edit"
 *    and post-create redirect are the two today). QA round 7, U2 (item 2) —
 *    `PlaybooksHome`'s "Open"/post-create and `PlaybooksConfigPage`'s guard
 *    both now route through {@link useGuardedFlowEntry} instead, which asks
 *    the extra question of whether the navigation would silently destroy a
 *    DIFFERENT flow's surviving drafts.
 *
 * A THIRD exit — the browser Back button — is deliberately NOT guarded, and
 * that is the whole point of this unit rather than an omission. `useBlocker`
 * needs a data router (react-router-dom 6.30.3) and the app mounts
 * `<BrowserRouter>`; a popstate listener runs after React Router has already
 * unmounted the shell; a sentinel-history-entry workaround false-fires on
 * every Back through a `?step=` entry. So Back is made HARMLESS instead:
 * `PlayFlowShell` no longer clears the draft stores on unmount, so Back
 * keeps everything and `UnsavedEditsPill` offers the way straight back. See
 * `flow-session-store.ts` for the other half.
 *
 * CAPTURE PHASE, not bubble: React Router's `<Link>` calls `preventDefault`
 * in its own bubble-phase handler and navigates. Listening in the capture
 * phase is the only place an app-level listener can still decide, and
 * `stopPropagation` there keeps the Link's handler from ever running.
 *
 * The listener is only installed while `dirty` — a clean surface adds no
 * document-level click cost and no behavior change at all.
 */

/**
 * The store-backed half of "is there unsaved work here": copy drafts and
 * generated copy awaiting review. Subscribes through boolean selectors
 * (`unsaved-guard.ts`), so typing inside an already-dirty step never
 * re-renders the guard or the header pill.
 *
 * Surface-local dirt (Config's `dirtySegmentationIds`) is NOT in here — that
 * surface ORs its own boolean with this hook's value before passing `dirty`.
 * Round-4 QA codex-review fix (wave 1, finding 3): Config must call this too,
 * not only pass its local set. `SequenceCopyPane` (Config's own sequence
 * editor) writes to the SAME global `sequence-copy-draft-store` the Play flow
 * does, at `draftScope="library"`, so a Config-side copy edit was previously
 * invisible to every exit on that page — no dialog, not even the
 * `beforeunload` prompt.
 *
 * R5-3 (playbooks-qa-round5-feedback.md) — `scope` is OPTIONAL and, when
 * omitted, this reads the WHOLE store exactly as before (the Play flow's own
 * call: its drafts genuinely span rung scopes — a rep's edits on one rung
 * must still arm the guard while they're looking at a different rung). When
 * supplied (Config passes `'library'`), this reads ONLY that scope's
 * entries, via `unsaved-guard.ts`'s scoped selectors — closing the false
 * positive where an unrelated leftover `rung:*`/`new:*` draft (kept alive on
 * purpose by the Play flow's Back-button design) made Config read dirty for
 * a page the rep never touched.
 */
export function useHasUnsavedWork(scope?: string): boolean {
  const hasCopyDrafts = useSequenceCopyDraftStore(
    scope !== undefined ? selectHasCopyDraftsInScope(scope) : selectHasCopyDrafts,
  );
  const hasReadyGeneratedCopy = useGeneratedCopyStore(
    scope !== undefined ? selectHasReadyGeneratedCopyInScope(scope) : selectHasReadyGeneratedCopy,
  );
  return hasCopyDrafts || hasReadyGeneratedCopy;
}

/**
 * Drops every piece of flow work the stores hold, plus the session record
 * behind the header pill. Explicit, and only ever called from a rep's own
 * Discard — never from an effect cleanup.
 *
 * Codex review of Playbooks QA round 5 (finding B1) — `scope` is OPTIONAL
 * and, when omitted, this is the ORIGINAL global wipe (the Play flow's own
 * click-guard call: a Play-flow Discard is understood to mean "discard this
 * whole flow session," which the pre-existing `clearFlowSession()` call
 * already reflects). When supplied (Config passes `'library'`), this drops
 * ONLY that scope's copy drafts and generated-copy reviews, leaving any
 * OTHER scope's entries — most importantly a genuinely surviving Play-flow
 * `rung:*`/`new:*` draft kept alive on purpose by the Back-button design —
 * untouched. `useHasUnsavedWork` (this file) started detecting dirt SCOPED
 * to `'library'` for Config (R5-3), but this action stayed a global wipe
 * until this fix — a Config "Discard changes" click was silently destroying
 * unrelated Play-flow work it never showed the rep a single word about.
 * `clearFlowSession()` is skipped for a scoped discard — it is a Play-flow-
 * only concern Config never touches in the first place.
 */
export function discardAllUnsavedWork(scope?: string): void {
  if (scope !== undefined) {
    clearCopyDraftsInScope(scope);
    clearGeneratedCopyReviewsInScope(scope);
    return;
  }
  clearSequenceCopyDrafts();
  clearGeneratedCopyReviews();
  clearFlowSession();
}

export interface UnsavedWorkGuardProps {
  dirty: boolean;
  /**
   * Whether the drafts this guard is protecting actually SURVIVE the
   * navigation it is about to intercept.
   *
   * `true` (the Play flow, the default) — `PlayFlowShell` no longer clears
   * the draft stores on unmount, so "Leave, keep edits" is a real, honest
   * third option: the work stays in module scope and `UnsavedEditsPill`
   * links back to it.
   *
   * `false` (Config) — round-4 QA codex-review fix, wave 1, finding 4. The
   * plan's one accepted U7 gap is that Config's condition drafts are
   * component-local `useState` and die on unmount; that is a scope decision,
   * NOT a licence for the dialog to claim otherwise. With `false` there is
   * nothing to keep, so the "Leave, keep edits" action is dropped entirely
   * (Stay / Discard only) and the copy stays surface-neutral instead of
   * promising "…edits in this Play".
   */
  draftsSurvive?: boolean;
  /** Forwarded to {@link useGuardedNavigate} — see that option's own doc. */
  discardScope?: string;
}

interface GuardedNavigateOptions {
  dirty: boolean;
  draftsSurvive?: boolean;
  /**
   * Codex review of Playbooks QA round 5 (finding B1) — forwarded verbatim
   * to {@link discardAllUnsavedWork} on "Discard changes". `undefined` (the
   * Play-flow click-guard's default) keeps the original global wipe; Config
   * passes `'library'` so its own Discard can never destroy a Play-flow
   * rung's surviving draft. See that function's own doc.
   */
  discardScope?: string;
  /**
   * QA round 7, U2 — overrides the dialog body. `undefined` keeps the
   * existing `draftsSurvive`-keyed default copy below; {@link useGuardedFlowEntry}
   * passes {@link FLOW_ENTRY_DISCARD_DESCRIPTION} for its own wipe guard.
   */
  description?: string;
}

/**
 * The guard's decision, as a plain callable: navigate to `href` now, or open
 * the dialog and let the rep decide.
 *
 * Extracted (round-4 QA codex-review fix, wave 1, finding 5) so it has TWO
 * kinds of caller instead of one. `UnsavedWorkGuard`'s capture-phase click
 * listener is the original; the second is a surface whose own code calls
 * `navigate()` imperatively (`PlaybooksConfigPage`'s row "Edit" and its
 * post-create redirect into a brand-new playbook's flow). Those bypassed the
 * listener completely — no anchor, no click, no dialog — and silently lost a
 * dirty segmentation draft, which is strictly worse than the accepted
 * "dialog on link clicks only" gap.
 */
export function useGuardedNavigate({
  dirty,
  draftsSurvive = true,
  discardScope,
  description,
}: GuardedNavigateOptions): (href: string) => void {
  const navigate = useNavigate();
  const { openAlertDialog, closeAlertDialog } = useAlertDialog();

  /**
   * The dialog's buttons are rendered once, into an imperative dialog that
   * outlives this render. Reading `navigate`/`closeAlertDialog` off a ref
   * keeps those handlers correct without re-installing the document listener
   * (and re-opening nothing) every time React Router hands us a new
   * `navigate` identity.
   */
  const handlersRef = useRef({ navigate, closeAlertDialog });
  handlersRef.current = { navigate, closeAlertDialog };

  return useCallback(
    (href: string) => {
      if (!dirty) {
        handlersRef.current.navigate(href);
        return;
      }

      const leave = () => {
        handlersRef.current.closeAlertDialog();
        handlersRef.current.navigate(href);
      };

      openAlertDialog({
        // R5-1 (playbooks-qa-round5-feedback.md) — `sm` (384px) is too narrow
        // for the 3-button `draftsSurvive` variant: three `lg`,
        // `whitespace-nowrap` buttons plus gaps overflow the 336px available
        // inside `sm`'s padding, spilling "Stay" almost to the card's start
        // edge. `md` (448px) comfortably fits three buttons; the 2-button
        // Config variant (`draftsSurvive: false`) already looks correct at
        // `sm` and stays there.
        size: draftsSurvive ? 'md' : 'sm',
        content: (
          <>
            <AlertDialogHeader onClose={() => handlersRef.current.closeAlertDialog()}>
              <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogBody className="flex flex-col gap-3">
              <AlertDialogDescription>
                {description ??
                  (draftsSurvive
                    ? 'You have unsaved edits in this Play. Leaving now discards them.'
                    : 'You have unsaved edits. Leaving now discards them.')}
              </AlertDialogDescription>
            </AlertDialogBody>
            <AlertDialogActions>
              <Button variant="secondary" onClick={() => handlersRef.current.closeAlertDialog()}>
                Stay
              </Button>
              {/*
                Only offered where it is TRUE. On a surface whose drafts do
                not survive the unmount, a "Leave, keep edits" button keeps
                nothing — see `draftsSurvive`'s own doc.
              */}
              {draftsSurvive ? (
                <Button variant="secondary" onClick={leave}>
                  Leave, keep edits
                </Button>
              ) : null}
              <Button
                variant="danger"
                onClick={() => {
                  // The shell's cleanup no longer clears anything, so the
                  // explicit clear has to happen HERE, before the navigation
                  // that unmounts the surface. Scoped per `discardScope` —
                  // see that prop's own doc (Codex review, round 5, B1).
                  discardAllUnsavedWork(discardScope);
                  leave();
                }}
              >
                Discard changes
              </Button>
            </AlertDialogActions>
          </>
        ),
      });
    },
    [dirty, draftsSurvive, discardScope, description, openAlertDialog],
  );
}

/** OQ11 — Spencer's wording call. Do not improve it. */
export const FLOW_ENTRY_DISCARD_DESCRIPTION =
  'You have unsaved edits in another Play. Opening this one discards them.';

/**
 * QA round 7, U2 (item 2) — {@link useGuardedNavigate} plus ONE more question:
 * is the rep about to mount a DIFFERENT flow while another flow's drafts are
 * surviving in module scope? Round 4 made Back harmless by keeping drafts
 * alive and showing `UnsavedEditsPill`; the other half of that design is
 * `PlayFlowShell`'s context-change clear, which wipes every store the moment
 * a different play/playbook mounts. Home's "Open" / post-create and Config's
 * row "Edit" / Library "Open" are exactly those mounts, and none of them
 * asked — the pill vanished and Play A's typing was gone. `dirty` here is
 * the host's OWN dirt (Config: library scope + segmentation drafts; Home:
 * `false`); the surviving-flow check is added on top and only for an href
 * whose flow differs from `lastContextKey`. Returning to the same flow is a
 * plain navigate: those drafts survive by design.
 */
export function useGuardedFlowEntry(opts: GuardedNavigateOptions): (href: string) => void {
  const ownGuard = useGuardedNavigate(opts);
  const wipeGuard = useGuardedNavigate({
    dirty: true,
    draftsSurvive: false,
    description: FLOW_ENTRY_DISCARD_DESCRIPTION,
  });
  const hasSurvivingWork = useHasUnsavedWork();
  const lastContextKey = useFlowSessionStore((state) => state.lastContextKey);
  return useCallback(
    (href: string) => {
      const target = flowContextKeyFromHref(href);
      if (hasSurvivingWork && target !== null && target !== lastContextKey) {
        wipeGuard(href);
        return;
      }
      ownGuard(href);
    },
    [ownGuard, wipeGuard, hasSurvivingWork, lastContextKey],
  );
}

export function UnsavedWorkGuard({ dirty, draftsSurvive = true, discardScope }: UnsavedWorkGuardProps) {
  useUnsavedChangesWarning(dirty);

  const guardedNavigate = useGuardedNavigate({ dirty, draftsSurvive, discardScope });

  useEffect(() => {
    if (!dirty) return;

    function onCapturedClick(event: MouseEvent) {
      // A modified click is the rep asking for a new tab/window, and a
      // non-primary button is not a navigation at all — neither loses the
      // draft in THIS tab, so neither is intercepted.
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('a[data-playbooks-nav]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== '_self') return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      event.preventDefault();
      event.stopPropagation();

      guardedNavigate(href);
    }

    document.addEventListener('click', onCapturedClick, true);
    return () => document.removeEventListener('click', onCapturedClick, true);
  }, [dirty, guardedNavigate]);

  return null;
}
